import { useHistory, useLocation } from 'react-router-dom';
import './SupportTypeSelector.scss';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { Button, Loading, Modal, ViewModal } from '../../../components';
import { DisplayUtils, StatusUtils } from '../../../utils';
import { useParametrics } from '../../../contexts/ParametricsContext';
import { getSupportType, saveSupportType } from '../../../services/petitionServices';
import { useErrorsContext } from '../../../contexts/ErrorsContext';
import { useAuthContext } from '../../../contexts/AuthContext';

const SupportTypeSelector = () => {
  const history = useHistory();
  const { t } = useTranslation();
  const { convocationId } = useParametrics();
  const { isSso } = useAuthContext();
  const { showError } = useErrorsContext();
  const location = useLocation();
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedSupportType, setSelectedSupportType] = useState('CONSUMER');
  const [petition, setPetition] = useState(null);

  const userType = location?.state?.userType;
  const petitionId = location?.state?.petitionId;
  const petitionHasAccount = location?.state?.petitionHasAccount;

  const getPetitionSupportType = async () => {
    try {
      setLoading(true);
      const result = await getSupportType(convocationId, userType.toUpperCase(), petitionId);
      setPetition(result);
      if (result.supportType) setSelectedSupportType(result.supportType);
    } catch (e) {
      showError(e);
    } finally {
      setLoading(false);
    }
  };

  const changeSupportType = async () => {
    try {
      setLoading(true);
      await saveSupportType(convocationId, userType.toUpperCase(), petitionId, {
        supportType: selectedSupportType,
      });
      if (isSso) history.push(`/${userType}/join/confirm-identity`, { confirmIdentity: true, userType, petitionId, petitionHasAccount, supportType: selectedSupportType });
      else history.push(`/${userType}/join/clave-verification`, { claveVerification: true, userType, petitionId, petitionHasAccount, supportType: selectedSupportType });
    } catch (e) {
      showError(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!convocationId) return;
    if (!location?.state?.selectSupportType) {
      history.goBack();
    } else {
      if (petitionId && userType) getPetitionSupportType();
      setShow(true);
    }
  }, [convocationId]); // eslint-disable-line

  const goBack = () => {
    history.push('/join/select-user-type', {
      selectUserType: true,
      userType: userType,
      petitionId: petitionId,
      petitionHasAccount,
    });
  };

  const onContinue = () => {
    if (petition && (petition.supportType !== selectedSupportType || petition.processStatus?.status === StatusUtils.STATUS.CHANGE_ACCOUNT_TYPE)) {
      if (petition.processStatus?.status === StatusUtils.STATUS.CHANGE_ACCOUNT_TYPE) {
        changeSupportType();
      } else {
        setShowConfirmModal(true);
      }
    } else {
      if (isSso) history.push(`/${userType}/join/confirm-identity`, { confirmIdentity: true, userType, petitionId, petitionHasAccount, supportType: selectedSupportType });
      else history.push(`/${userType}/join/clave-verification`, { claveVerification: true, userType, petitionId, petitionHasAccount, supportType: selectedSupportType });
    }
  };

  return (
    show && (
      loading ? (
        <div className='vh-100 d-flex justify-content-center align-items-center'>
          <Loading />
        </div>
      ) : (
        <>
          <div className='SupportTypeSelector'>
            <div className="w-100 d-flex justify-content-center">
              {!DisplayUtils.isMobile() ? (
                <img src="/misc/icons/complete_logo_white.svg" alt="shop-icon" className="SupportTypeSelector__logo" />
              ) : (
                <div className="SupportTypeSelector__title">
                  <Button
                    icon="/misc/icons/arrow-left-blue.svg"
                    action={goBack}
                    className="p-2 button-only-icon me-2"
                    a11y={t('Globals.return')}
                  />
                  <h1>{t('Join.selectSupportType.title')}</h1>
                </div>
              )}
            </div>
            <div className="w-100 d-flex justify-content-center">
              <div className="SupportTypeSelector__container">
                {!DisplayUtils.isMobile() && (
                  <div className="SupportTypeSelector__container__head">
                    <Button
                      icon="/misc/icons/arrow-left-pink.svg"
                      action={goBack}
                      className="p-2 button-only-icon me-2"
                      a11y={t('Globals.return')}
                    />
                    <h1>{t('Join.selectSupportType.title')}</h1>
                  </div>
                )}
                <p className="SupportTypeSelector__container__description">
                  <b>{t('Join.selectSupportType.consumerHeader')}</b><br/>
                  {t('Join.selectSupportType.consumerDescription')}
                </p>
                <p className="SupportTypeSelector__container__description">
                  <b>{t('Join.selectSupportType.creatorHeader')}</b><br/>
                  {t('Join.selectSupportType.creatorDescription')}
                </p>
                <p className="SupportTypeSelector__container__description">
                  {t('Join.selectSupportType.moreInfo')}<a target="_blank" rel="noopener noreferrer" href="https://culture-bonus-public-prod.s3.eu-central-1.amazonaws.com/BCJ_2026_informacion_modalidades_solicitud_vf.pdf">{t('Join.selectSupportType.moreInfoLink')}</a>
                </p>
                <div className="cards">
                  <Card
                    title={t('Join.selectSupportType.consumer')}
                    subtitle={t('Join.selectSupportType.consumerSubtitle')}
                    selected={selectedSupportType === 'CONSUMER'}
                    supportType="consumer"
                    onSelect={() => setSelectedSupportType('CONSUMER')}
                  />
                  <Card
                    title={t('Join.selectSupportType.creator')}
                    subtitle={t('Join.selectSupportType.creatorSubtitle')}
                    selected={selectedSupportType === 'CREATOR'}
                    supportType="creator"
                    onSelect={() => setSelectedSupportType('CREATOR')}
                  />
                </div>
                <div className="SupportTypeSelector__container__actions">
                  <Button
                    className="buttonPrimaryLarge"
                    text={t('Join.selectSupportType.continue')}
                    styles={{ backgroundColor: '#34fdfe', color: '#381940' }}
                    action={onContinue}
                  />
                </div>
                <button
                  className="SupportTypeSelector__container__later"
                  onClick={() => history.push({ pathname: `/join/leave`, state: { leave: true } })}
                >
                  {t('Join.selectSupportType.later')}
                </button>
              </div>
            </div>
          </div>
          <Modal
            isShowingModal={showConfirmModal}
            component={(
              <ViewModal
                imageSource={'/misc/icons/warning.png'}
                title={t('Join.selectSupportType.confirmationModal.title')}
                description={t('Join.selectSupportType.confirmationModal.description')}
                action={() => {
                  setShowConfirmModal(false);
                  changeSupportType();
                }}
                secondAction={() => {
                  setShowConfirmModal(false);
                }}
                textBtn={t('Join.selectSupportType.confirmationModal.textButton')}
                secondTextBtn={t('Join.selectSupportType.confirmationModal.secondTextButton')}
              />
            )}
          />
        </>
      )
    )
  );
};

const Card = ({ title, subtitle, selected, onSelect, supportType }) => {
  return (
    <button
      onClick={onSelect}
      className="d-flex flex-column align-items-center flex-grow-1 support-card"
    >
      <img className='avatar' src={`/misc/icons/supportType/support-type-selector-${supportType}.png`} alt=" " />
      <p className='text'>{title}</p>
      <img
        className='check'
        src={selected ? '/misc/icons/radio_active.svg' : '/misc/icons/radio.svg'}
        alt=" "
      />
      <p className='subtitle'>{subtitle}</p>
    </button>
  );
};

export default SupportTypeSelector;
